import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
    ArrowLeft,
    UserPlus01
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Button } from '@/components/base/buttons/button';
import { TextArea } from '@/components/base/textarea/textarea';
import { DatePicker } from '@/components/application/date-picker/date-picker';
import { Label } from '@/components/base/input/label';
import { orderService } from '@/data/mock-orders';
import { collaborationService } from '@/data/mock-collaborations';
import { accountService } from '@/data/mock-accounts';
import { activityService } from '@/data/mock-activities';
import { useTranslation } from 'react-i18next';
import { getWeekNumber } from '@/utils/week-utils';
import { storage } from '@/lib/local-storage';
import { today, getLocalTimeZone, CalendarDate } from '@internationalized/date';

interface ShipperOption {
    id: string;
    label: string;
    email: string;
    country: string;
}

export const ReceiverNewOrderPage = () => {
    const { t } = useTranslation(['pages:orders', 'common']);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [shipperOptions, setShipperOptions] = useState<ShipperOption[]>([]);
    const [showNewShipperForm, setShowNewShipperForm] = useState(false);

    const [formData, setFormData] = useState({
        shipperId: '',
        targetDate: null as CalendarDate | null,
        packingListRequested: '',
        notes: '',
        // New shipper form
        newShipperName: '',
        newShipperEmail: '',
        newShipperCountry: ''
    });

    useEffect(() => {
        const loadShippers = async () => {
            const accountId = storage.getItem('accountId') || '2';

            // Load active collaborations
            const collaborations = collaborationService.getByReceiverId(accountId);
            const activeCollaborations = collaborations.filter(c => c.status === 'Active');

            // Load shipper accounts
            const allAccounts = accountService.getAll();
            const shipperAccounts = allAccounts.filter(acc => acc.userType === 'SHIPPER');

            // Create options from active collaborations
            const options: ShipperOption[] = activeCollaborations.map(collab => {
                const shipper = shipperAccounts.find(acc => acc.id === collab.shipperId);
                return shipper ? {
                    id: shipper.id,
                    label: shipper.legalName,
                    email: shipper.email,
                    country: shipper.country
                } : null;
            }).filter(Boolean) as ShipperOption[];

            setShipperOptions(options);
        };
        loadShippers();
    }, []);

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleCreateNewShipper = async () => {
        if (!formData.newShipperName || !formData.newShipperEmail) {
            setError(t('form.errors.allFieldsRequired', { ns: 'common' }));
            return;
        }

        // In a real app, this would create a new shipper account and collaboration
        // For now, we'll just show an error message
        setError(t('form.errors.newShipperNotImplemented'));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            // Validation
            if (!formData.shipperId || !formData.targetDate || !formData.packingListRequested.trim()) {
                setError(t('form.errors.allFieldsRequired', { ns: 'common' }));
                setIsLoading(false);
                return;
            }

            const accountId = storage.getItem('accountId') || '2';
            const targetDate = formData.targetDate.toDate(getLocalTimeZone());
            const targetWeek = getWeekNumber(targetDate);
            const targetYear = targetDate.getFullYear();

            // Create order
            const newOrder = orderService.create({
                receiverId: accountId,
                shipperId: formData.shipperId,
                targetWeek,
                targetYear,
                packingList: {
                    requested: formData.packingListRequested
                },
                notes: formData.notes || undefined
            });

            // Create activity for shipper
            const receiverAccount = accountService.getById(accountId);
            const shipperAccount = accountService.getById(formData.shipperId);
            if (receiverAccount && shipperAccount) {
                activityService.add({
                    id: `order-${newOrder.id}`,
                    audience: 'shipper',
                    type: 'new_order_request',
                    title: `Nuova richiesta ordine da ${receiverAccount.legalName}`,
                    description: `Richiesta ordine con target settimana ${targetWeek}/${targetYear}. Packing list richiesta con dettagli completi.`,
                    timestamp: new Date(),
                    isRead: false,
                    actions: [
                        { label: 'Vedi dettaglio ordine', href: `/shipper/orders/${newOrder.id}` }
                    ],
                    metadata: {
                        orderId: newOrder.id,
                        receiverId: accountId,
                        shipperId: formData.shipperId
                    }
                });
            }

            navigate(`/receiver/orders/${newOrder.id}`);
        } catch (err) {
            console.error('Error creating order:', err);
            setError(t('form.errors.generic', { ns: 'common' }));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button
                    onClick={() => navigate('/receiver/orders')}
                    color="secondary"
                    size="sm"
                    iconLeading={ArrowLeft}
                >
                    {t('form.back')}
                </Button>
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('form.title')}</h1>
                    <p className="text-tertiary mt-1">{t('form.subtitle')}</p>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-6">{t('form.shipperSection')}</h2>

                    {!showNewShipperForm ? (
                        <div className="space-y-4">
                            <Select
                                label={t('form.shipper')}
                                items={shipperOptions}
                                selectedKey={formData.shipperId}
                                onSelectionChange={(key) => handleInputChange('shipperId', key)}
                                isRequired
                                size="md"
                                placeholder={t('form.shipperPlaceholder')}
                            >
                                {(item) => (
                                    <Select.Item key={item.id} id={item.id}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select>

                            <Button
                                type="button"
                                onClick={() => setShowNewShipperForm(true)}
                                color="secondary"
                                size="md"
                                iconLeading={UserPlus01}
                            >
                                {t('form.createNewShipper')}
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <Input
                                label={t('form.newShipperName')}
                                placeholder={t('form.newShipperNamePlaceholder')}
                                value={formData.newShipperName}
                                onChange={(value) => handleInputChange('newShipperName', value)}
                                isRequired
                                size="md"
                            />
                            <Input
                                label={t('form.newShipperEmail')}
                                placeholder={t('form.newShipperEmailPlaceholder')}
                                value={formData.newShipperEmail}
                                onChange={(value) => handleInputChange('newShipperEmail', value)}
                                isRequired
                                size="md"
                                type="email"
                            />
                            <Input
                                label={t('form.newShipperCountry')}
                                placeholder={t('form.newShipperCountryPlaceholder')}
                                value={formData.newShipperCountry}
                                onChange={(value) => handleInputChange('newShipperCountry', value)}
                                size="md"
                            />
                            <div className="flex gap-3">
                                <Button
                                    type="button"
                                    onClick={() => {
                                        setShowNewShipperForm(false);
                                        setFormData(prev => ({
                                            ...prev,
                                            newShipperName: '',
                                            newShipperEmail: '',
                                            newShipperCountry: ''
                                        }));
                                    }}
                                    color="secondary"
                                    size="md"
                                >
                                    {t('form.cancel')}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleCreateNewShipper}
                                    color="primary"
                                    size="md"
                                >
                                    {t('form.createShipper')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-6">{t('form.orderSection')}</h2>

                    <div className="space-y-4">
                        <div>
                            <Label isRequired>{t('form.targetWeek')}</Label>
                            <DatePicker
                                value={formData.targetDate}
                                onChange={(date) => handleInputChange('targetDate', date)}
                                minValue={today(getLocalTimeZone())}
                            />
                        </div>

                        <TextArea
                            label={t('form.packingList')}
                            placeholder={t('form.packingListPlaceholder')}
                            value={formData.packingListRequested}
                            onChange={(value) => handleInputChange('packingListRequested', value)}
                            isRequired
                            rows={6}
                        />

                        <TextArea
                            label={t('form.notes')}
                            placeholder={t('form.notesPlaceholder')}
                            value={formData.notes}
                            onChange={(value) => handleInputChange('notes', value)}
                            rows={4}
                        />
                    </div>
                </div>

                {error && (
                    <div className="rounded-lg bg-error-50 p-4 border border-error-200">
                        <p className="text-sm text-error-700">{error}</p>
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={() => navigate('/receiver/orders')}
                        color="secondary"
                        size="md"
                    >
                        {t('form.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        size="md"
                        isDisabled={isLoading || !formData.shipperId || !formData.targetDate || !formData.packingListRequested.trim()}
                    >
                        {isLoading ? t('form.saving', { ns: 'common' }) : t('form.submit')}
                    </Button>
                </div>
            </form>
        </div>
    );
};

